import type { MapLocation, LocationType } from '@/entities/dashboard/mapping';
import type { LocationDisplay } from './types';

export class LocationModel implements MapLocation {
  constructor(
    public readonly id: string,
    public readonly name: string,
    public readonly type: LocationType,
    public readonly lat: number,
    public readonly lng: number,
    public readonly siteId: string,
    public readonly regionId: string,
    public readonly subregionId: string,
    public readonly countryId: string
  ) {}

  static fromResponse(data: MapLocation): LocationModel {
    return new LocationModel(
      data.id,
      data.name,
      data.type,
      data.lat,
      data.lng,
      String(data.siteId),
      String(data.regionId),
      String(data.subregionId),
      String(data.countryId)
    );
  }

  get displayName(): string {
    return this.name.replace(/\s*\(.*?\)\s*/g, '').trim();
  }

  get typeName(): string {
    const typeNames: Record<string, string> = {
      W: 'Warehouse',
      P: 'Port',
      C: 'Customer',
      S: 'Site'
    };
    return typeNames[this.type] || 'Unknown';
  }

  get coordinates(): [number, number] {
    return [this.lat, this.lng];
  }

  get icon(): string {
    const icons: Record<string, string> = {
      W: 'warehouse',
      P: 'truck',
      C: 'users',
      S: 'map-pin'
    };
    return icons[this.type] || 'map-pin';
  }

  get color(): string {
    const colors: Record<string, string> = {
      W: 'blue',
      P: 'green',
      C: 'orange',
      S: 'purple'
    };
    return colors[this.type] || 'gray';
  }

  toDisplay(): LocationDisplay {
    return {
      id: this.id,
      name: this.displayName,
      fullName: this.name,
      type: this.type,
      typeName: this.typeName,
      coordinates: this.coordinates,
      icon: this.icon,
      color: this.color
    };
  }

  distanceTo(other: LocationModel): number {
    const R = 6371;
    const dLat = this.toRad(other.lat - this.lat);
    const dLon = this.toRad(other.lng - this.lng);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(this.lat)) * Math.cos(this.toRad(other.lat)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private toRad(deg: number): number {
    return (deg * Math.PI) / 180;
  }
}
